import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, CircularProgress } from '@material-ui/core'
import { usePortfolioData } from '../PortfolioDataProvider'
import { useUtilityPanel } from '../UtilityPanelProvider'
import { buildLevelRequest, buildRowRequest } from './allocation/buildAllocationRequest'
import { useAllocationQuery } from './allocation/useAllocationQuery'
import AxisPicker from './scatter/AxisPicker'
import ScatterChart from './scatter/ScatterChart'
import ScatterDetailTable from './scatter/ScatterDetailTable'
import { deriveAxisOptions } from './scatter/scatterEligibility'

// Cross-sectional 2D comparison tool. Plots two measures against each
// other across the rows at a chosen grouping level.
//
// Reuses Allocation's request + query pipeline verbatim -- Allocation
// already emits a single multi-calc grouping request (one calc per
// `config.columns` entry) and flattens the response to per-row items
// with every measure keyed by columnInstance.id. Scatter consumes the
// same `items` shape and projects two of those measures onto the X / Y
// axes; the picker can swap axes without re-firing the request because
// the cached response already contains every column's value.

const useStyles = makeStyles(() => ({
  body: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 14,
    overflow: 'auto'
  },
  loading: {
    flex: 1,
    minHeight: 280,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    color: 'var(--color-neutral-500, #6b7280)',
    fontSize: 13
  },
  error: {
    flex: 1,
    minHeight: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 24px',
    textAlign: 'center',
    color: 'var(--color-danger-600, #c62828)',
    fontSize: 13
  },
  empty: {
    flex: 1,
    minHeight: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 24px',
    textAlign: 'center',
    color: 'var(--color-neutral-500, #6b7280)',
    fontSize: 13
  }
}))

// Pick a sensible default Y axis. Priority:
//   1. The columnInstance from the payload, if eligible.
//   2. The second eligible option (so the X / Y defaults aren't the
//      same column when launched without a Y hint).
//   3. The first eligible option, if there's only one.
function pickDefaultAxes (axisOptions, payload) {
  if (axisOptions.length === 0) return { axisXId: null, axisYId: null }
  const payloadId = payload?.columnInstance?.id
  const payloadIsEligible = !!payloadId && axisOptions.some(o => o.columnInstance.id === payloadId)
  const axisYId = payloadIsEligible
    ? payloadId
    : (axisOptions[1]?.columnInstance.id || axisOptions[0]?.columnInstance.id)
  const axisXId = axisOptions.find(o => o.columnInstance.id !== axisYId)?.columnInstance.id ||
    axisOptions[0]?.columnInstance.id
  return { axisXId, axisYId }
}

export default function ScatterTool ({ payload }) {
  const classes = useStyles()
  const {
    config,
    asOfDate,
    columnDefinitions,
    groupings,
    prefs,
    clientId,
    entity,
    entityIds
  } = usePortfolioData()
  const { setHeaderMeta, registerToolActions } = useUtilityPanel()

  // -- axis pool --
  const axisOptions = useMemo(
    () => deriveAxisOptions(config, columnDefinitions, prefs),
    [config, columnDefinitions, prefs]
  )

  // -- axis state --
  // Initial pick is payload-aware; subsequent picks live in tool state
  // until the payload identity changes. State is adjusted during render
  // via the "previous prop key" pattern -- on a payload identity flip
  // we synchronously setState during render, and React discards the
  // in-progress render and re-renders with the fresh defaults. Avoids
  // the wasted commit + effect cycle of a `useEffect` reset, and
  // collapses what used to be two effects (init/reset on payload,
  // recovery on options change) into linear render-time code.
  //
  // See React docs "Adjusting some state when a prop changes":
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [axisXId, setAxisXId] = useState(null)
  const [axisYId, setAxisYId] = useState(null)

  const payloadKey = useMemo(() => {
    if (!payload) return null
    if (payload.scope === 'header') return `header|${payload.columnInstance?.id ?? ''}`
    if (payload.scope === 'row') {
      const rowKey = payload.row?.groupEntityId != null ? `id:${payload.row.groupEntityId}` : (payload.row?.name ?? '')
      return `row|${rowKey}|${payload.columnInstance?.id ?? ''}`
    }
    if (payload.scope === 'level') return `level|${payload.depth ?? ''}`
    return null
  }, [payload])

  // Track the payloadKey we last initialized for. A mismatch means the
  // user just opened the tool on a different cell/header/segment;
  // recompute defaults and reset axis state. First render: prevKey
  // starts at the same value the initializer captures, so the block
  // doesn't fire spuriously.
  const [prevPayloadKey, setPrevPayloadKey] = useState(payloadKey)
  if (payloadKey !== prevPayloadKey) {
    setPrevPayloadKey(payloadKey)
    const defaults = pickDefaultAxes(axisOptions, payload)
    setAxisXId(defaults.axisXId)
    setAxisYId(defaults.axisYId)
  }

  // Recovery: if the user edits config.columns out from under the tool
  // (or axisOptions hadn't loaded yet at init time), snap stale axis
  // ids to a valid option. Render-phase setState bails out via Object.is
  // when the chosen value already matches state, so the steady-state
  // case is free; the one-off correction is a single re-render.
  if (axisOptions.length > 0) {
    const xValid = axisXId && axisOptions.some(o => o.columnInstance.id === axisXId)
    if (!xValid) {
      const xFallback = axisOptions.find(o => o.columnInstance.id !== axisYId)?.columnInstance.id ?? axisOptions[0].columnInstance.id
      if (xFallback !== axisXId) setAxisXId(xFallback)
    }
    const yValid = axisYId && axisOptions.some(o => o.columnInstance.id === axisYId)
    if (!yValid) {
      const yFallback = axisOptions.find(o => o.columnInstance.id !== axisXId)?.columnInstance.id ?? axisOptions[0].columnInstance.id
      if (yFallback !== axisYId) setAxisYId(yFallback)
    }
  }

  // Enforce mutual exclusion at the change handler level (in addition
  // to the picker's disabled-options UI). If the user picks X = Y,
  // bump the other axis to the next available option.
  const handleAxisXChange = useCallback((nextX) => {
    setAxisXId(nextX)
    if (nextX === axisYId) {
      const fallback = axisOptions.find(o => o.columnInstance.id !== nextX)?.columnInstance.id
      if (fallback) setAxisYId(fallback)
    }
  }, [axisYId, axisOptions])

  const handleAxisYChange = useCallback((nextY) => {
    setAxisYId(nextY)
    if (nextY === axisXId) {
      const fallback = axisOptions.find(o => o.columnInstance.id !== nextY)?.columnInstance.id
      if (fallback) setAxisXId(fallback)
    }
  }, [axisXId, axisOptions])

  // -- request synthesis --
  // Reuse the allocation builders verbatim:
  //   - 'header' (right-click column header): show top-level grouping.
  //   - 'level' (right-click breadcrumb segment): show that grouping.
  //   - 'row' (right-click cell): show children of the row at the next
  //     grouping level (allocation's buildRowRequest already handles
  //     entity-scopable vs virtual-grouping dispatch).
  const built = useMemo(() => {
    if (!payload || !config || !asOfDate || !entity || !entityIds) return null
    if (payload.scope === 'header') {
      return buildLevelRequest({ config, asOfDate, entity, entityIds, depth: 0 })
    }
    if (payload.scope === 'level') {
      return buildLevelRequest({ config, asOfDate, entity, entityIds, depth: payload.depth })
    }
    if (payload.scope === 'row') {
      return buildRowRequest({ config, asOfDate, entity, entityIds, row: payload.row })
    }
    return null
  }, [payload, config, asOfDate, entity, entityIds])

  const request = built?.request || null
  const unwrap = built?.unwrap || null

  const globalDateConfig = useMemo(() => ({
    dateMode: config?.dateMode,
    quickDate: config?.quickDate,
    startDate: config?.startDate,
    endDate: config?.endDate
  }), [config?.dateMode, config?.quickDate, config?.startDate, config?.endDate])

  const { items, isFetching, error } = useAllocationQuery({
    request,
    clientId,
    columnInstances: config?.columns,
    globalDateConfig,
    entity,
    entityIds,
    unwrap
  })

  // -- axis meta lookups --
  const axisXMeta = useMemo(
    () => axisOptions.find(o => o.columnInstance.id === axisXId) || null,
    [axisOptions, axisXId]
  )
  const axisYMeta = useMemo(
    () => axisOptions.find(o => o.columnInstance.id === axisYId) || null,
    [axisOptions, axisYId]
  )

  // -- project items into [{ name, groupId, x, y }] --
  // Items have all measures keyed by columnInstance.id. Rows with both
  // X and Y numeric become chart points; rows missing either still
  // appear in the detail table (with -- placeholders) so the user can
  // see why a name they expected isn't on the chart.
  const allRows = useMemo(() => {
    if (!items?.length || !axisXId || !axisYId) return []
    return items.map(it => ({
      name: it.name,
      groupId: it.groupId,
      x: typeof it[axisXId] === 'number' ? it[axisXId] : null,
      y: typeof it[axisYId] === 'number' ? it[axisYId] : null
    }))
  }, [items, axisXId, axisYId])

  const chartPoints = useMemo(
    () => allRows.filter(r => typeof r.x === 'number' && typeof r.y === 'number'),
    [allRows]
  )

  // -- header metadata --
  useEffect(() => {
    const groupingLabel = payload?.groupingLabel || groupings?.[payload?.depth] || 'Compare'
    const parentName = payload?.scope === 'row' ? payload?.row?.name : null
    const yLabel = axisYMeta?.label || 'Y'
    const xLabel = axisXMeta?.label || 'X'
    const title = parentName
      ? `${yLabel} vs ${xLabel} in ${parentName}`
      : `${yLabel} vs ${xLabel}`

    let subjectLine
    if (isFetching) {
      subjectLine = 'Loading...'
    } else if (error) {
      subjectLine = 'Failed to load'
    } else {
      const n = chartPoints.length
      const itemText = `${n} ${n === 1 ? 'group' : 'groups'} at ${groupingLabel}`
      subjectLine = asOfDate ? `${itemText} . as of ${asOfDate}` : itemText
    }
    setHeaderMeta({ eyebrow: 'Compare', title, subjectLine })
  }, [payload, groupings, axisXMeta, axisYMeta, isFetching, error, chartPoints.length, asOfDate, setHeaderMeta])

  // -- toolbar actions --
  useEffect(() => {
    if (axisOptions.length < 2) {
      registerToolActions(null)
      return undefined
    }
    registerToolActions(
      <AxisPicker
        axisOptions={axisOptions}
        axisXId={axisXId}
        axisYId={axisYId}
        onAxisXChange={handleAxisXChange}
        onAxisYChange={handleAxisYChange}
      />
    )
    return () => registerToolActions(null)
  }, [axisOptions, axisXId, axisYId, handleAxisXChange, handleAxisYChange, registerToolActions])

  // -- render --
  if (axisOptions.length < 2) {
    return (
      <div className={classes.empty}>
        Add at least two comparable measures to your view to compare them on a scatter chart.
      </div>
    )
  }

  if (!request) {
    return (
      <div className={classes.empty}>
        No grouping level available to compare at.
      </div>
    )
  }

  if (error) {
    return (
      <div className={classes.error}>
        <span>Could not load comparison.</span>
        <span>{error?.message || 'Unknown error'}</span>
      </div>
    )
  }

  if (isFetching && allRows.length === 0) {
    return (
      <div className={classes.loading}>
        <CircularProgress size={18} />
        <span>Loading comparison...</span>
      </div>
    )
  }

  if (!axisXMeta || !axisYMeta) {
    return (
      <div className={classes.empty}>
        Pick two measures from the toolbar to compare.
      </div>
    )
  }

  return (
    <div className={classes.body}>
      <ScatterChart
        points={chartPoints}
        axisXLabel={axisXMeta.label}
        axisYLabel={axisYMeta.label}
        axisXFormat={axisXMeta.valueFormat}
        axisYFormat={axisYMeta.valueFormat}
        height={340}
      />
      <ScatterDetailTable
        rows={allRows}
        axisXLabel={axisXMeta.label}
        axisYLabel={axisYMeta.label}
        axisXFormat={axisXMeta.valueFormat}
        axisYFormat={axisYMeta.valueFormat}
      />
    </div>
  )
}

ScatterTool.propTypes = {
  payload: PropTypes.shape({
    scope: PropTypes.oneOf(['header', 'level', 'row']).isRequired,
    columnInstance: PropTypes.object,
    row: PropTypes.object,
    depth: PropTypes.number,
    groupingLabel: PropTypes.string
  })
}
